import React, { useState, useEffect } from 'react';
import {
  Card,
  Button,
  Upload,
  Select,
  Input,
  message,
  List,
  Tag,
  Space,
  Modal,
  Typography,
  Alert,
  Divider,
  Spin,
  Tooltip
} from 'antd';
import {
  UploadOutlined,
  FileTextOutlined,
  DownloadOutlined,
  DeleteOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { documentService, DocumentMetadata } from '../services/documentService';
import { useAuth } from '../hooks/useAuth';

const { TextArea } = Input;
const { Text } = Typography;

interface Document {
  id: string;
  fileName: string;
  fileType: string;
  documentType: string;
  notes?: string;
  uploadedBy: string;
  uploadedAt: string;
  fileSize: number;
  downloadUrl?: string;
}

interface DocumentUploadProps {
  appointmentId: string;
  onDocumentUploaded?: () => void;
}

const DocumentUpload: React.FC<DocumentUploadProps> = ({
  appointmentId,
  onDocumentUploaded
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [documents, setDocuments] = useState<DocumentMetadata[]>([]);
  const [isUploadModalVisible, setIsUploadModalVisible] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [documentType, setDocumentType] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [loading, setLoading] = useState(true);

  // Document types for medical documents
  const documentTypes = [
    { value: 'sick_leave_certificate', label: t('appointments.documentTypes.sick_leave_certificate') },
    { value: 'prescription', label: t('appointments.documentTypes.prescription') },
    { value: 'lab_report', label: t('appointments.documentTypes.lab_report') },
    { value: 'medical_record', label: t('appointments.documentTypes.medical_record') },
    { value: 'imaging', label: t('appointments.documentTypes.imaging') },
    { value: 'referral', label: t('appointments.documentTypes.referral') },
    { value: 'discharge_summary', label: t('appointments.documentTypes.discharge_summary') },
    { value: 'consent_form', label: t('appointments.documentTypes.consent_form') },
    { value: 'other', label: t('appointments.documentTypes.other') }
  ];

  const handleFileSelect = (file: File) => {
    const validation = documentService.validateFile(file);
    
    if (!validation.isValid) {
      message.error(validation.error);
      return false;
    }

    // Process the file for UTF-8 compatibility and store the processed version
    const processedFile = documentService.processFileForUTF8(file);
    setSelectedFile(processedFile);
    return false; // Prevent default upload behavior
  };

  const handleUpload = async () => {
    if (!selectedFile || !documentType) {
      message.error(t('appointments.documentUpload.selectTypeAndFile'));
      return;
    }

    setUploading(true);
    try {
      const uploadResult = await documentService.uploadDocument(
        selectedFile,
        documentType,
        notes,
        appointmentId,
        user?.email || 'Unknown User'
      );

      if (uploadResult.success) {
        // Reload documents to get the updated list
        const updatedDocs = await documentService.listDocuments(appointmentId);
        setDocuments(updatedDocs);
        
        message.success(t('appointments.documentUpload.uploadSuccess'));
        
        // Reset form
        setSelectedFile(null);
        setDocumentType('');
        setNotes('');
        setIsUploadModalVisible(false);
        
        // Notify parent component
        onDocumentUploaded?.();
      } else {
        message.error(uploadResult.error || t('appointments.documentUpload.uploadError'));
      }
    } catch (error) {
      console.error('Upload error:', error);
      message.error(t('appointments.documentUpload.uploadError'));
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteDocument = async (documentId: string) => {
    try {
      const document = documents.find(doc => doc.id === documentId);
      if (!document) {
        message.error('Document not found');
        return;
      }

      const deleteResult = await documentService.deleteDocument(documentId, document.gcpBucketPath);
      
      if (deleteResult.success) {
        // Remove from local state
        setDocuments(prev => prev.filter(doc => doc.id !== documentId));
        message.success(t('appointments.documentUpload.deleteSuccess'));
      } else {
        message.error(deleteResult.error || t('appointments.documentUpload.deleteError'));
      }
    } catch (error) {
      console.error('Delete error:', error);
      message.error(t('appointments.documentUpload.deleteError'));
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatUploadDate = (timestamp: string) => {
    try {
      const date = new Date(timestamp);
      const now = new Date();
      const diffInHours = Math.abs(now.getTime() - date.getTime()) / (1000 * 60 * 60);
      
      if (diffInHours < 24) {
        // Today or yesterday
        if (date.toDateString() === now.toDateString()) {
          return `Today at ${date.toLocaleTimeString('en-US', { 
            hour: 'numeric', 
            minute: '2-digit',
            hour12: true 
          })}`;
        } else if (date.toDateString() === new Date(now.getTime() - 24 * 60 * 60 * 1000).toDateString()) {
          return `Yesterday at ${date.toLocaleTimeString('en-US', { 
            hour: 'numeric', 
            minute: '2-digit',
            hour12: true 
          })}`;
        }
      }
      
      // For older dates, show date and time
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      // Fallback to simple formatting if date parsing fails
      return new Date(timestamp).toLocaleString();
    }
  };

  const getDocumentTypeLabel = (type: string) => {
    const docType = documentTypes.find(dt => dt.value === type);
    return docType ? docType.label : type;
  };

  // Load documents when component mounts
  useEffect(() => {
    const loadDocuments = async () => {
      try {
        const docs = await documentService.listDocuments(appointmentId);
        setDocuments(docs);
      } catch (error) {
        console.error('Error loading documents:', error);
        message.error('Failed to load documents');
      } finally {
        setLoading(false);
      }
    };

    if (appointmentId) {
      loadDocuments();
    }
  }, [appointmentId]);

  const uploadProps = {
    beforeUpload: handleFileSelect,
    showUploadList: false,
    accept: '.pdf,.jpg,.jpeg,.png,.gif,.doc,.docx,.txt'
  };

  return (
    <>
      <Card
        title={
          <Space>
            <FileTextOutlined />
            {t('appointments.documents.title')}
          </Space>
        }
        extra={
          <Button
            type="primary"
            icon={<UploadOutlined />}
            onClick={() => setIsUploadModalVisible(true)}
          >
            {t('appointments.documents.upload')}
          </Button>
        }
        style={{ marginBottom: 24 }}
      >
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <Spin size="large" />
            <div style={{ marginTop: 16, color: '#666' }}>
              {t('common.loading')}
            </div>
          </div>
        ) : documents.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <FileTextOutlined style={{ fontSize: 48, color: '#d9d9d9', marginBottom: 16 }} />
            <div style={{ color: '#666', marginBottom: 8 }}>
              {t('appointments.documents.noDocuments')}
            </div>
            <div style={{ color: '#999', fontSize: 12 }}>
              {t('appointments.documents.uploadHint')}
            </div>
          </div>
        ) : (
          <List
            dataSource={documents}
            renderItem={(document) => (
              <List.Item
                actions={[
                  <Button
                    key="download"
                    type="link"
                    icon={<DownloadOutlined />}
                    onClick={() => window.open(document.downloadUrl, 'noopener')}
                    disabled={!document.downloadUrl || document.downloadUrl === '#'}
                  >
                    {t('appointments.documents.download')}
                  </Button>,
                  <Button
                    key="delete"
                    type="link"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => handleDeleteDocument(document.id)}
                  >
                    {t('common.delete')}
                  </Button>
                ]}
              >
                <List.Item.Meta
                  avatar={<FileTextOutlined style={{ fontSize: 24, color: '#1890ff' }} />}
                  title={
                    <Space>
                      <Tooltip title={document.originalName || document.fileName}>
                        <span style={{ 
                          maxWidth: 200, 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis', 
                          whiteSpace: 'nowrap',
                          display: 'inline-block'
                        }}>
                          {document.originalName || document.fileName}
                        </span>
                      </Tooltip>
                      <Tag color="blue">{getDocumentTypeLabel(document.documentType)}</Tag>
                      <Tag color="default">{formatFileSize(document.fileSize)}</Tag>
                    </Space>
                  }
                  description={
                    <div>
                      {document.notes && (
                        <div style={{ marginBottom: 8 }}>
                          <Text type="secondary">{document.notes}</Text>
                        </div>
                      )}
                      <div style={{ fontSize: 12, color: '#666' }}>
                        {t('appointments.documents.uploadedBy')}: {document.uploadedBy} | 
                        {t('appointments.documents.uploadedAt')}: {formatUploadDate(document.uploadedAt)}
                      </div>
                      {document.originalName && document.originalName !== document.fileName && (
                        <div style={{ fontSize: 11, color: '#999', marginTop: 4 }}>
                          {t('appointments.documents.storedAs')}: {document.fileName}
                        </div>
                      )}
                    </div>
                  }
                />
              </List.Item>
            )}
          />
        )}
      </Card>

      {/* Upload Modal */}
      <Modal
        title={t('appointments.documents.uploadDocument')}
        open={isUploadModalVisible}
        onOk={handleUpload}
        onCancel={() => {
          setIsUploadModalVisible(false);
          setSelectedFile(null);
          setDocumentType('');
          setNotes('');
        }}
        okButtonProps={{ loading: uploading }}
        okText={t('appointments.documents.upload')}
        cancelText={t('common.cancel')}
        width={600}
      >
        <div style={{ marginBottom: 24 }}>
          <Alert
            message={t('appointments.documents.uploadInfo')}
            description={
              <div>
                <div>{t('appointments.documents.uploadInfoDesc1')}</div>
                <div>{t('appointments.documents.uploadInfoDesc2')}</div>
                <div>{t('appointments.documents.uploadInfoDesc3')}</div>
              </div>
            }
            type="info"
            showIcon
            icon={<InfoCircleOutlined />}
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 8 }}>
            <Text strong>{t('appointments.documents.documentType')} *</Text>
          </div>
          <Select
            style={{ width: '100%' }}
            placeholder={t('appointments.documents.selectDocumentType')}
            value={documentType}
            onChange={setDocumentType}
            options={documentTypes}
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 8 }}>
            <Text strong>{t('appointments.documents.file')} *</Text>
          </div>
          <Upload {...uploadProps}>
            <Button icon={<UploadOutlined />}>
              {selectedFile ? selectedFile.name : t('appointments.documents.selectFile')}
            </Button>
          </Upload>
          {selectedFile && (
            <div style={{ marginTop: 8, fontSize: 12, color: '#666' }}>
              {t('appointments.documents.fileSizeLimit')}
            </div>
          )}
        </div>

        <div style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 8 }}>
            <Text>{t('appointments.documents.notes')}</Text>
          </div>
          <TextArea
            rows={3}
            placeholder={t('appointments.documents.notesPlaceholder')}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>
      </Modal>
    </>
  );
};

export default DocumentUpload;
